import { BoardPresets, boardPresets } from 'modules/template-editor/domain/consts/form/boards/boardPresets';
import { Values } from 'modules/template-editor/domain/interface/form/board';
import { eventsOptions, typeOptions } from 'modules/template-editor/ui/components/elements/components/events-selector/type';
import BoardForm from 'modules/template-editor/ui/components/elements/forms/board-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';

import { AppDispatch } from 'store/store';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { copyBoard } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';

const PRESET = BoardPresets.duplicate;

function useCopyModal() {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { activeBoard, boards, isProcessed, isFinished, process } = useBoards();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: Values) => {
      if (!activeBoard) return;
      const { name } = values;

      dispatchWithToast(
        copyBoard({
          boardId: activeBoard.id,
          name: name.trim(),
          templateId: activeBoard.templateId,
          process: 'copy board',
        }),
        { error: 'Не удалось создать копию' },
      );
    },
    [activeBoard, dispatchWithToast],
  );

  const initialValue = useMemo(() => {
    const events = activeBoard
      ? (eventsOptions.filter((option) => activeBoard?.events?.includes(option.value)) as Values['events'])
      : undefined;

    return {
      name: activeBoard?.name ? activeBoard?.name + ' (дубликат)' : '',
      type: typeOptions.find((type) => type.value === activeBoard?.type),
      events: events,
      duration: activeBoard?.duration.toString(),
    } as Values;
  }, [activeBoard]);

  const existingNames = useMemo(() => boards.map((board) => board.name.trim()), [boards]);

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (process !== 'copy board') {
      return;
    }
    close();
    dispatch(resetPressedState());
    if (!activeBoard?.templateId) {
      return;
    }
  }, [activeBoard?.templateId, close, dispatch, isFinished, process]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} noOverflow size="md">
        <UDText type={'title-3'} className="mb-5">
          {boardPresets[PRESET].header}
        </UDText>
        <BoardForm
          onSubmit={onSubmit}
          initialValues={initialValue}
          isLoading={isProcessed}
          preset={PRESET}
          existingNames={existingNames}
          disableImport
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCopyModal;
